import { usePersistentState } from "../../hooks/use-persistent-state";
import { STATUS_META, type Batch } from "./types";
import { Play, Pause, XCircle, LayoutGrid, ChevronDown, ChevronRight } from "lucide-react";
import { useBatchAction } from "./queries";
import { MultiSelect } from "../../components/ui/multi-select";
import { useMemo, useState } from "react";
import React from "react";

const TABLE_COLUMNS = [
  { id: "status", label: "Status" },
  { id: "progress", label: "Progress" },
  { id: "concurrency", label: "Concurrency" },
  { id: "agent", label: "Agent" },
  { id: "version", label: "Version" },
  { id: "language", label: "Language" },
] as const;
type ColumnId = typeof TABLE_COLUMNS[number]["id"];

export function BatchesTable({
  batches,
  onRowClick,
  filtersSlot,
  variant = "workspace",
}: {
  batches: Batch[];
  onRowClick: (b: Batch) => void;
  filtersSlot?: React.ReactNode;
  variant?: "workspace" | "workflow";
}) {
  const mutation = useBatchAction();
  const [columns, setColumns] = usePersistentState<ColumnId[]>(
    "batches:columns", 
    ["status", "progress", "concurrency", "agent"]
  );

  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({
    queued: true,
    running: true,
    scheduled: true,
    paused: true,
    completed: true,
    failed: true,
    other: true,
  });

  const toggleGroup = (groupId: string) => {
    setExpandedGroups(prev => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  const groupOrder = variant === "workflow"
    ? ["queued", "completed"]
    : ["running", "scheduled", "paused", "completed", "failed", "other"];
  const groupedBatches = useMemo(() => {
    const groups: Record<string, Batch[]> = {
      queued: [],
      running: [],
      scheduled: [],
      paused: [],
      completed: [],
      failed: [],
      other: [],
    };
    batches.forEach(b => {
      if (variant === "workflow") {
        if (b.status === "queued" || b.status === "running") groups.queued.push(b);
        else if (b.status === "completed") groups.completed.push(b);
        return;
      }
      if (groups[b.status]) {
        groups[b.status].push(b);
      } else {
        groups.other.push(b);
      }
    });
    return groups;
  }, [batches, variant]);

  return (
    <div className="w-full h-full flex flex-col gap-4 overflow-hidden">
      <div className="flex justify-end items-center gap-2 flex-none">
        {filtersSlot}
        <MultiSelect
          options={TABLE_COLUMNS.map(c => ({ label: c.label, value: c.id }))}
          selected={new Set(columns)}
          onChange={(vals) => setColumns(Array.from(vals) as ColumnId[])}
          buttonLabel=""
          defaultSelected={["status", "progress", "concurrency", "agent"]}
          icon={<LayoutGrid className="w-4 h-4 mr-2" />}
        />
      </div>
      
      <div className="w-full flex-1 overflow-auto bg-canvas rounded-xl border border-line">
        <table className="w-full text-left text-sm text-ink">
          <thead className="bg-elevated border-b border-line text-xs uppercase text-ink-3">
            <tr>
              <th className="px-4 py-3 font-medium">Name</th>
              {columns.includes("language") && <th className="px-4 py-3 font-medium">Language</th>}
              {columns.includes("status") && <th className="px-4 py-3 font-medium">Status</th>}
              {columns.includes("progress") && <th className="px-4 py-3 font-medium">Progress</th>}
              {columns.includes("concurrency") && <th className="px-4 py-3 font-medium">Concurrency</th>}
              {columns.includes("agent") && <th className="px-4 py-3 font-medium">Agent</th>}
              {columns.includes("version") && <th className="px-4 py-3 font-medium">Version</th>}
              <th className="px-4 py-3 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {batches.length === 0 ? (
              <tr>
                <td colSpan={columns.length + 2} className="px-4 py-8 text-center text-ink-3">
                  No batches found.
                </td>
              </tr>
            ) : (
              groupOrder.map((groupId) => {
                const groupBatches = groupedBatches[groupId];
                if (groupId === "other" && groupBatches.length === 0) return null;
                const isExpanded = expandedGroups[groupId];

                return (
                  <React.Fragment key={groupId}>
                    <tr 
                      className="bg-elevated border-y border-line cursor-pointer transition-colors hover:bg-soft shadow-sm"
                      onClick={() => toggleGroup(groupId)}
                    >
                      <td colSpan={columns.length + 2} className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button className="flex h-6 w-6 items-center justify-center rounded-lg hover:bg-soft text-ink-3">
                            {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                          </button>
                          <span className="h-2 w-2 rounded-full" style={{ background: STATUS_META[groupId as keyof typeof STATUS_META]?.dot || "#a8a099" }} />
                          <span className="text-sm font-bold text-ink capitalize">
                            {variant === "workflow" && groupId === "queued" ? "Running" : groupId}
                          </span>
                          <span className="rounded-full bg-soft px-2 py-0.5 font-mono text-[10px] font-bold text-ink-2 border border-line">
                            {groupBatches.length}
                          </span>
                        </div>
                      </td>
                    </tr>
                    {isExpanded && groupBatches.map((batch) => {
                      const isTerminal = ["completed", "failed", "cancelled"].includes(batch.status);
                      const progressPercent = batch.total > 0 ? (batch.completed / batch.total) * 100 : 0;

                      return (
                        <tr 
                          key={batch.schedule_id} 
                          onClick={() => onRowClick(batch)}
                          className="hover:bg-soft cursor-pointer group transition-colors"
                        >
                          <td className="px-4 py-3 font-medium text-ink pl-8">
                            <span className="truncate max-w-[200px] block">{batch.name}</span>
                          </td>
                          {columns.includes("language") && (
                            <td className="px-4 py-3">
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-soft text-ink-2 border border-line">
                                {batch.language}
                              </span>
                            </td>
                          )}
                          {columns.includes("status") && (
                            <td className="px-4 py-3">
                              <span className={`capitalize px-2 py-1 rounded-full text-xs ${
                                batch.status === "running" ? "bg-success-soft text-success border border-success/20" :
                                batch.status === "paused" ? "bg-warning-soft text-warning border border-warning/20" :
                                batch.status === "failed" ? "bg-danger-soft text-danger border border-danger/20" :
                                "bg-soft text-ink-2 border border-line"
                              }`}>
                                {batch.status}
                              </span>
                            </td>
                          )}
                          {columns.includes("progress") && (
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-3">
                                <div className="w-24 h-1.5 bg-line rounded-full overflow-hidden">
                                  <div className="h-full bg-success" style={{ width: `${progressPercent}%` }} />
                                </div>
                                <span className="text-xs text-ink-3 w-12 text-right">{Math.round(progressPercent)}%</span>
                              </div>
                            </td>
                          )}
                          {columns.includes("concurrency") && (
                            <td className="px-4 py-3 text-ink-2">
                              {batch.concurrency}
                            </td>
                          )}
                          {columns.includes("agent") && (
                            <td className="px-4 py-3 text-ink-2 truncate max-w-[150px]">
                              {batch.agent}
                            </td>
                          )}
                          {columns.includes("version") && (
                            <td className="px-4 py-3 text-ink-2 truncate max-w-[150px]">
                              {batch.version}
                            </td>
                          )}
                          <td className="px-4 py-3 text-right">
                            {!isTerminal && (
                              <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                {batch.status === "running" || batch.status === "scheduled" ? (
                                  <button 
                                    onClick={(e) => { e.stopPropagation(); mutation.mutate({ id: batch.schedule_id, patch: { status: "paused" } }); }}
                                    className="p-1.5 hover:bg-soft rounded text-warning transition-colors"
                                    title="Pause Batch"
                                  >
                                    <Pause className="w-4 h-4" />
                                  </button>
                                ) : batch.status === "paused" ? (
                                  <button 
                                    onClick={(e) => { 
                                      e.stopPropagation(); 
                                      mutation.mutate({ id: batch.schedule_id, patch: { status: "resume" } });
                                    }}
                                    className="p-1.5 hover:bg-soft rounded text-success transition-colors"
                                    title="Resume Batch"
                                  >
                                    <Play className="w-4 h-4" />
                                  </button>
                                ) : null}
                                {batch.status === "paused" && (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); mutation.mutate({ id: batch.schedule_id, patch: { status: "completed" } }); }}
                                    className="p-1.5 hover:bg-soft rounded text-danger transition-colors"
                                    title="Cancel Remainder"
                                  >
                                    <XCircle className="w-4 h-4" />
                                  </button>
                                )}
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
